import {Component} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRoute, ParamMap} from '@angular/router';
import {EMPTY, Observable, forkJoin} from 'rxjs';
import {catchError, map, switchMap, tap} from "rxjs/operators";

import {DoiService} from '../../doi/DoiModule';

import {KlaraObjectView} from '../../klara/KlaraModule';

import {ProcessActTypeService} from '../model/ProcessActTypeService';
import {ProcessActTypePart, ProcessActTypeObject} from '../model/ProcessActTypeObject';
import {ProcessPart, ProcessObject, ProcessService} from '../../process/ProcessModule';
import {KlaraDialogTopView} from '../../klara/KlaraModule';

/**
 *
 */
@Component({
	templateUrl: 'ProcessActTypeObjectView.html'
})
export class ProcessActTypeObjectView extends KlaraObjectView<ProcessActTypeObject> implements KlaraDialogTopView
{
	name = 'ProcessActTypeObjectView';

	/**
	 * Template for a new object.
	 */
	objectTemplate: ProcessActTypeObject;

	/**
	 * Construct a new object view.
	 */
	constructor(doi: DoiService, private processActTypeService: ProcessActTypeService, route: ActivatedRoute, private processService: ProcessService)
	{
		super(doi, processActTypeService, route);
	}

	/**
	 * Return the corresponding path in Klara Dialog, null if not available or an empty string for the same URL.
	 */
	dialogPath(): string
	{
		if (this.object && this.object.objectID)
			return '/processacttype/'+this.object.objectID+'/general';
		else
			return null;
	}

	/**
	 * Test if an object panel is editable when in edit mode.
	 * Overridden to check panel name and user permissions.
	 */
	objectPanelEditable(panelName: string): boolean
	{
		switch (panelName) {
			case 'auditlogentry':
				return false;
			case 'units':
				return false;
			case 'detail':
				return this.editingAsUser();
			default:
				return this.editingAsEditorOrUser();
		}
	}


	routerLinkArchiveOrig()
	{
		return this.object && this.object.process && this.object.process.processStructure && this.object.process.processStructure.archiveOrig &&
				['/archiveorig', this.object.process.processStructure.archiveOrigID];
	}

	routerLinkProcessStructure()
	{
		return this.object.process && ['/processstructure', this.object.process.procStructID];
	}

	routerLinkProcess()
	{
		return this.object.processID ? ['/process', this.object.processID] : null;
	}

	/**
	 * Create and return an observable that when subscribed will refresh the view.
	 * Overridden to refresh object parts presented by the object view itself.
	 */
	refreshView(): Observable<any>
	{
		if (this.doi.auth.isAuthenticating())
			return EMPTY;

		//Saknar process, vid ny handlingstyp
		if (!this.object.process)
			this.object.process = this.processService.newObject(null);

		return forkJoin(
			this.refreshObjectPart(ProcessActTypePart.Label).pipe(
				switchMap(acttype => {
				this.appView.selectProcessStructure(acttype.procStructID);
					return this.processActTypeService.parentObjectObservable(acttype);
				})
			),
			this.probeObjectPart(ProcessActTypePart.General),
			this.probeObjectPart(ProcessActTypePart.Units),
			this.probeObjectPart(ProcessActTypePart.ProcActTypeSeries),
			super.refreshView()
		);
	}

	/**
	 * Test if any of the object parts used by the specified object panel has been found to have backend values as a result of an object probe. If not
	 * known, undefined is returned.
	 * Overriddene to invoke objectPartHasValues with the part name or part names used by the panel.
	 */
	probedPanel(panelName: string): boolean
	{
		switch (panelName) {
			case 'general': return this.probedPart('General');
			case 'detail': return this.probedPart('General');
			case 'units': return (this.probedPart('Units') || this.probedPart('Series'));
			case 'auditlogentry': return true;
		}
	}

	/**
	 * Return a title describing the type of view without data dependencies.
	 */
	typeTitle(): string
	{
		return 'Handlingstyp'; // I18N
	}

	/**
	 * Create an observable that when subscribed starts editing the object and activates the optional subview.
	 * Overridden to fetch property values for edit before switching to the super implementation.
	 * @param subviewName The name of the subview to activate.
	 */
	editStartObservable(subviewName?: string): Observable<string>
	{
		if (!this.object.propertyValues)
			return super.editStartObservable();

		return this.processActTypeService.readPropertyValuesForEdit(this.objectID, this.object).pipe(
			switchMap((processacttype) => {
				return super.editStartObservable(subviewName);
			})
		);
	}
}

// GtLHY+JUzyXoT9DpYNumwFk0PRA=
